import React from "react"
import { useTranslation } from "react-i18next"
import { Grid, Typography, styled } from "@mui/material"

import { useAppDispatch, useAppSelector } from "redux/hooks"
import { selectFetchStatus } from "redux/features/client/clientSlice"
import {
  makeResendEmailConfirmation,
  makeCancelEmailConfirmation,
} from "redux/features/client/emailConfirmationThunk"
import { showAlert, showNotice } from "redux/features/notice/noticeSlice"
import Button from "components/ui/Button"
import { ClientConfirmationFormValues } from "types"

type Props = React.HTMLAttributes<HTMLDivElement> & {
  confirmation: ClientConfirmationFormValues
  onConfirmation: () => void
}

const EmailConfirmationForm = ({ confirmation, onConfirmation }: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const fetchStatus = useAppSelector((state) => selectFetchStatus(state))
  const { unconfirmed_email } = confirmation
  const isUnconfirmedEmail = unconfirmed_email && unconfirmed_email.length > 0

  const handleResend = () => {
    if (!fetchStatus.isFetching) {
      dispatch(makeResendEmailConfirmation())
        .then((result) => {
          dispatch(showNotice({ message: result }))
          onConfirmation()
        })
        .catch((error) => {
          dispatch(showAlert({ message: error }))
        })
    }
  }

  const handleCancel = () => {
    if (!fetchStatus.isFetching) {
      dispatch(makeCancelEmailConfirmation())
        .then((result) => {
          dispatch(showNotice({ message: result }))
          onConfirmation()
        })
        .catch((error) => {
          dispatch(showAlert({ message: error }))
        })
    }
  }

  return (
    <>
      {isUnconfirmedEmail && (
        <Wrapper
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Item>
            <Title>
              {t("features.client.confirmation.email.title", {
                unconfirmed_email,
              })}
            </Title>
            <Resend onClick={handleResend}>
              {t("features.client.confirmation.email.resend")}
            </Resend>
            <Cancel onClick={handleCancel}>
              {t("features.client.confirmation.email.cancel")}
            </Cancel>
          </Item>
        </Wrapper>
      )}
    </>
  )
}

export default EmailConfirmationForm

const Title = styled(Typography)`
  padding: 4px 0px;
  text-align: left;
`
const Wrapper = styled(Grid)`
  padding: 4px 4px 0 4px;
`
const Item = styled(Grid)`
  display: flex;
  flex-direction: row;
  margin: 0;
`
const Resend = styled(Button)`
  background: rgba(0, 169, 149, 0.07);
  color: ${({ theme }) => theme.palette.primary.main};
  border-radius: 21px;
  font-weight: 600;
  height: 56px;
  max-width: 175px;
  padding: 0 12px;
  margin: 0px 4px;
  width: auto;
`
const Cancel = styled(Button)`
  background: rgba(172, 172, 172, 0.1);
  color: ${({ theme }) => theme.palette.common.black};
  border-radius: 21px;
  font-weight: 600;
  height: 56px;
  max-width: 175px;
  padding: 0 12px;
  margin: 0px 4px;
  width: auto;
`
